'use client'

import posthog from 'posthog-js'
import { PostHogProvider as Provider } from 'posthog-js/react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { useSession } from '@/app/lib/auth-client'

// Initialize PostHog once on the client side
if (typeof window !== 'undefined') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
    capture_pageview: false, // We capture pageviews manually
    loaded: (ph) => { // Use a parameter name like 'ph' to avoid shadowing the imported 'posthog'
      // Web Vitals might initialize correctly once PostHog is fully loaded.
      // Enable debug mode only after loading, if in development.
      if (process.env.NODE_ENV === 'development') {
        ph.debug(); // Call debug on the loaded instance
      }
    }
  })
}

// Separate component for page view tracking
function PostHogPageview() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (typeof window === 'undefined' || !pathname) return;

    // Construct the URL with search params
    const url = searchParams?.toString()
      ? `${window.location.origin}${pathname}?${searchParams}`
      : `${window.location.origin}${pathname}`;

    // Capture pageview with full URL
    posthog.capture('$pageview', {
      $current_url: url,
      path: pathname,
    });
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Use Better Auth session hook
  const { data: sessionData, isPending, error } = useSession(); 

  useEffect(() => {
    // If pending, do nothing yet
    if (isPending) {
      return;
    }

    // If not pending and data exists, user is authenticated
    if (sessionData?.user) {
      const user = sessionData.user;
      posthog.identify(user.id, { 
        email: user.email, 
        name: user.name,   
        // Add any other relevant user properties
      });
    } 
    // If not pending and no data (and no error), user is unauthenticated
    else if (!sessionData && !error) {
      posthog.reset();
    }
    // Optional: Handle error state if needed
    else if (error) {
       console.error("Error fetching session for PostHog:", error);
       posthog.reset(); // Reset if session fetch failed
    }

  }, [sessionData, isPending, error]); // Re-run when session data, pending state, or error changes

  return (
    <Provider client={posthog}>
      {children}
      <PostHogPageview />
    </Provider>
  )
} 